"use client";
import {
  ReactNode,
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
  memo,
} from "react";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import {
  Bell,
  Menu,
  ShoppingBag,
  User,
  X,
  HelpCircle,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import {
  NotificationDrawer,
  UserMenuDropdown,
  Notification as DashboardNotification,
  NavItem,
  SidebarItem,
  Role,
  THEME,
} from "./dashboardComponents";
import { UserType } from "@/types/inde_menu";

const ROLE_THEME = {
  customer: {
    accent: THEME.colors.info,
    badgeBg: THEME.colors.info,
    badgeText: THEME.colors.white,
    sidebarBg: "#F0F9FF",
    menuHover: "#E0F2FE",
    logoBorder: THEME.colors.info,
    hero: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1200&auto=format",
    primary: THEME.colors.primary,
    accentLight: THEME.colors.secondaryLight,
    text: THEME.colors.darkText,
    mutedText: THEME.colors.mutedText,
    heroBg: "#E0F2FE",
  },
  waiter: {
    accent: THEME.colors.success,
    badgeBg: THEME.colors.success,
    badgeText: THEME.colors.white,
    sidebarBg: "#F0FDF4",
    menuHover: "#DCFCE7",
    logoBorder: THEME.colors.success,
    hero: "https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1200&auto=format",
    primary: THEME.colors.success,
    accentLight: THEME.colors.successLight,
    text: THEME.colors.darkText,
    mutedText: THEME.colors.mutedText,
    heroBg: "#DCFCE7",
  },
  vendor: {
    accent: THEME.colors.secondary,
    badgeBg: THEME.colors.secondary,
    badgeText: THEME.colors.white,
    sidebarBg: "#FFF7ED",
    menuHover: "#FFEDD5",
    logoBorder: THEME.colors.secondary,
    hero: "https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?w=1200&auto=format",
    primary: THEME.colors.success,
    accentLight: THEME.colors.successLight,
    text: THEME.colors.darkText,
    mutedText: THEME.colors.mutedText,
    heroBg: "#FFEDD5",
  },
  admin: {
    accent: "#8b5cf6",
    badgeBg: "#8b5cf6",
    badgeText: THEME.colors.white,
    sidebarBg: "#F5F3FF",
    menuHover: "#EDE9FE",
    logoBorder: "#8b5cf6",
    hero: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&auto=format",
    primary: THEME.colors.info,
    accentLight: THEME.colors.infoLight,
    text: THEME.colors.darkText,
    mutedText: THEME.colors.mutedText,
    heroBg: "#EDE9FE",
  },
} as const;

const DEFAULT_THEME = {
  primary: THEME.colors.primary,
  accent: THEME.colors.secondary,
  accentLight: THEME.colors.secondaryLight,
  sidebarBg: THEME.colors.cardBg,
  text: THEME.colors.darkText,
  mutedText: THEME.colors.mutedText,
  heroBg: "#F3F4F6",
};

type Notification = DashboardNotification;

interface ModernDashboardLayoutProps {
  children: ReactNode;
  navItems: NavItem[];
  sidebarItems: SidebarItem[];
  headerTitle?: string;
  targetRole: Role;
  user: UserType;
  onLogout: () => void;
  onProfileClick: () => void;
  onCartClick?: (item: any) => void;
  onNotificationClick?: (notification: Notification) => void;
  cartItemCount?: number;
  notificationsCount?: number;
  notifications?: Notification[];
  isLoading?: boolean;
  profileHref?: string;
  sessionId?: string;
}

type HeroBannerProps = {
  roleTheme: (typeof ROLE_THEME)[Role];
  targetRole: Role;
};

const HeroBanner = memo(({ roleTheme, targetRole }: HeroBannerProps) => {
  const roleLabel = useMemo(
    () => targetRole.charAt(0).toUpperCase() + targetRole.slice(1),
    [targetRole]
  );

  const formattedDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const description = useMemo(() => {
    switch (targetRole) {
      case "customer":
        return "Manage your orders and reservations efficiently from one central location.";
      case "waiter":
        return "Stay on top of tables and customers with ease.";
      case "vendor":
        return "Control menu items and orders seamlessly.";
      default:
        return "Oversee restaurant operations effortlessly.";
    }
  }, [targetRole]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
      className="relative w-full h-[180px] sm:h-[200px] rounded-xl overflow-hidden mb-6 shadow-sm border"
      style={{ backgroundColor: roleTheme.heroBg || DEFAULT_THEME.heroBg }}
    >
      {" "}
      {/* Background image */}
      {roleTheme.hero && (
        <Image
          src={roleTheme.hero}
          alt={`${roleLabel} dashboard banner`}
          fill
          className="object-cover"
          priority
          sizes="(max-width:640px) 100vw, (max-width:1024px) 80vw, 1200px"
        />
      )}
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
      <div className="absolute inset-0 flex flex-col justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.1,
            type: "spring",
            stiffness: 100,
            damping: 20,
          }}
          className="max-w-3xl"
        >
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <motion.span
              whileHover={{ scale: 1.05 }}
              className="px-3 py-1 text-sm font-semibold rounded-full"
              style={{
                background: roleTheme.badgeBg,
                color: roleTheme.badgeText,
              }}
            >
              <h2 className="text-sm font-semibold">{roleLabel} Dashboard</h2>
            </motion.span>

            <div className="flex items-center gap-2 text-sm font-medium  text-white/90">
              <span className="w-1.5 h-1.5 rounded-full  bg-white/70" />
              {formattedDate}
            </div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-base text-white/80 max-w-xl"
          >
            {description}
          </motion.p>
        </motion.div>
      </div>
    </motion.section>
  );
});

HeroBanner.displayName = "HeroBanner";

const CurrentTime = memo(() => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  return (
    <p className="text-sm text-gray-500">
      {currentTime.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}
    </p>
  );
});

CurrentTime.displayName = "CurrentTime";

const GlassSidebar = memo(
  ({
    profileHref,
    sidebarItems,
    currentPath,
    isMobile = false,
    open = false,
    setOpen,
    roleTheme,
  }: any) => {
    return (
      <>
        <AnimatePresence>
          {isMobile && open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/30 z-40 lg:hidden"
              onClick={() => setOpen(false)}
            />
          )}
        </AnimatePresence>

        <motion.div
          initial={isMobile ? { x: "-100%" } : { x: 0 }}
          animate={isMobile ? { x: open ? 0 : "-100%" } : { x: 0 }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className={`
          fixed lg:sticky inset-y-0 left-0 z-50 flex flex-col
          bg-white border-r border-gray-200
          ${isMobile ? "w-[70%] max-w-sm lg:hidden" : "w-72 hidden lg:flex"}
          h-screen top-0
        `}
        >
          <div className="p-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <Link href="/" className="flex items-center gap-2">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  className="h-15 w-50 rounded-full overflow-hidden flex items-center justify-center" 
                >
                  <Image
                    src="/logo.svg"
                    alt="Inde Menu"
                    width={130}
                    height={130}
                    className="object-contain"
                  />
                </motion.div>
              </Link>

              {isMobile && (
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              )}
            </div>
          </div>
          <nav className="flex-1 px-3 py-6 overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider px-3 mb-3">
                Main Menu
              </h3>
              <ul className="space-y-1">
                {sidebarItems.map((item: any) => {
                  const isActive = currentPath === item.link;
                  return (
                    <li key={item.link}>
                      <Link
                        href={item.link}
                        className={`flex items-center px-3 py-3 rounded-xl text-base font-medium transition-all ${
                          isActive
                            ? "text-white"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                        }`}
                        style={{
                          backgroundColor: isActive ? roleTheme.accent : "transparent",
                        }}
                        onClick={() => isMobile && setOpen(false)}
                      >
                        <span className="mr-3 opacity-90">{item.icon}</span>
                        <span>{item.label}</span>
                        {isActive && (
                          <motion.div
                            layoutId="activeSidebarItem"
                            className="ml-auto w-2 h-2 rounded-full bg-white/80"
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                            }}
                          />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider px-3 mb-3">
                Tools
              </h3>
              <ul className="space-y-1">
                <li>
                  <Link href={profileHref || "#"} className="block">
                    <button className="flex items-center w-full px-3 py-3 rounded-xl text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all">
                      <User size={24} className="mr-3 opacity-90" />
                      Profile
                    </button>
                  </Link>
                </li>
              </ul>
            </div>
          </nav>

          <div className="p-4 border-t border-gray-200 mt-auto">
            <button className="flex items-center w-full px-3 py-3 text-base font-medium text-gray-600 hover:text-blue-600 rounded-xl hover:bg-gray-100 transition-all">
              <HelpCircle size={20} className="mr-3 opacity-90" />
              Help & Support
            </button>
          </div>
        </motion.div>
      </>
    );
  }
);

GlassSidebar.displayName = "GlassSidebar";

const EnhancedMobileNav = memo(({ navItems, pathname, roleTheme }: any) => {
  return (
    <motion.nav
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="lg:hidden fixed bottom-4 left-4 right-4 h-16 bg-white rounded-2xl border border-gray-200 shadow-lg flex items-center justify-between px-4 z-30"
    >
      {navItems.slice(0, 4).map((item: any) => {
        const isActive = pathname === item.link;
        return (
          <Link
            key={item.link}
            href={item.link}
            className="flex-1 flex justify-center"
          >
            <div
              className={`flex flex-col items-center justify-center w-full relative ${
                isActive ? "text-blue-600" : "text-gray-500"
              }`}
            >
              <div className="w-7 h-7 flex items-center justify-center">
                {item.icon}
              </div>
              <span className="text-sm mt-1 truncate max-w-full">
                {item.label}
              </span>
              {isActive && (
                <motion.div
                  layoutId="activeBottomTab"
                  className="absolute -top-1 w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: roleTheme?.accent || DEFAULT_THEME.accent,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30,
                  }}
                />
              )}
            </div>
          </Link>
        );
      })}
    </motion.nav>
  );
});

EnhancedMobileNav.displayName = "EnhancedMobileNav";

function ModernDashboardLayout({
  children,
  navItems,
  sidebarItems,
  headerTitle,
  targetRole,
  user,
  onLogout,
  onProfileClick,
  onCartClick,
  onNotificationClick,
  cartItemCount = 0,
  notificationsCount = 0,
  notifications = [],
  isLoading: propIsLoading,
  profileHref,
}: ModernDashboardLayoutProps) {
  const pathname = usePathname();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(propIsLoading || false);
  const [isMobile, setIsMobile] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const roleTheme = useMemo(
    () => ROLE_THEME[targetRole] || DEFAULT_THEME,
    [targetRole]
  );

  const roleLabel = useMemo(
    () =>
      targetRole
        ? targetRole === "vendor"
          ? "Restaurant"
          : targetRole.charAt(0).toUpperCase() + targetRole.slice(1)
        : "User",
    [targetRole]
  );

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 1024);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      setSidebarOpen(false);
      setNotificationDrawerOpen(false);
      setUserMenuOpen(false);
      setIsLoading(true);
      const timer = setTimeout(() => setIsLoading(false), 400);
      return () => clearTimeout(timer);
    };
    handleRouteChange();
  }, [pathname]);

  const closeMenus = useCallback(
    (e: MouseEvent) => {
      if (
        e.target instanceof HTMLElement &&
        !e.target.closest('[data-menu="user"]') &&
        !e.target.closest('[data-menu="notifications"]') &&
        !e.target.closest('[data-menu="sidebar"]')
      ) {
        setUserMenuOpen(false);
        setNotificationDrawerOpen(false);
        if (isMobile) setSidebarOpen(false);
      }
    },
    [isMobile]
  );

  useEffect(() => {
    if (userMenuOpen || notificationDrawerOpen || sidebarOpen) {
      document.addEventListener("mousedown", closeMenus);
      return () => document.removeEventListener("mousedown", closeMenus);
    }
    return () => {};
  }, [userMenuOpen, notificationDrawerOpen, sidebarOpen, closeMenus]);

  useEffect(() => {
    if (sidebarOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen, isMobile]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSidebarOpen(false);
        setNotificationDrawerOpen(false);
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <div className="min-h-screen flex bg-gray-50 relative">
      <div data-menu="sidebar" ref={sidebarRef}>
        <GlassSidebar
          userDetails={user}
          profileHref={profileHref}
          sidebarItems={sidebarItems}
          currentPath={pathname}
          targetRole={targetRole}
          isMobile={isMobile}
          open={sidebarOpen}
          setOpen={setSidebarOpen}
          roleTheme={roleTheme}
        />
      </div>

      <div className="flex-1 flex flex-col min-h-screen lg:ml-0 relative z-10">
        <header
          className={`
            sticky top-0 left-0 right-0 h-16 flex items-center justify-between 
            px-4 sm:px-6 z-30 transition-all duration-300
            ${scrolled ? "shadow-sm bg-white" : "bg-white"}
          `}
          style={{
            borderBottom: `1px solid ${roleTheme.accent}20`,
          }}
        >
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={24} className="text-gray-700" />
            </button>

            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-gray-800 truncate">
                {headerTitle || `${roleLabel} Dashboard`}
              </h1>
              <CurrentTime />
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {targetRole === "customer" && (
              <button
                className="relative p-2.5 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={
                  onCartClick ? () => onCartClick(undefined as any) : undefined
                }
                aria-label="View cart"
              >
                <ShoppingBag size={24} />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-sm font-bold rounded-full shadow-sm">
                    {cartItemCount > 99 ? "99+" : cartItemCount}
                  </span>
                )}
              </button>
            )}

            <div className="relative" data-menu="notifications">
              <button
                className="relative p-2.5 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setNotificationDrawerOpen((prev) => !prev);
                  setUserMenuOpen(false);
                }}
                aria-label="View notifications"
                aria-expanded={notificationDrawerOpen}
              >
                <Bell size={24} />
                {notificationsCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-sm font-bold rounded-full shadow-sm"
                    style={{
                      backgroundColor: roleTheme.accent,
                      color: "#fff",
                    }}
                  >
                    {notificationsCount > 99 ? "99+" : notificationsCount}
                  </span>
                )}
              </button>

              <NotificationDrawer
                open={notificationDrawerOpen}
                onClose={() => setNotificationDrawerOpen(false)}
                notifications={notifications}
                accent={roleTheme.accent}
                onNotificationClick={onNotificationClick}
              />
            </div>

            <div className="relative" data-menu="user">
              <button
                className="flex items-center gap-2 px-3 py-1.5 ml-1 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen((prev) => !prev);
                  setNotificationDrawerOpen(false);
                }}
                aria-label="User menu"
                aria-expanded={userMenuOpen}
              >
                <div
                  className="w-10 h-10 rounded-full overflow-hidden border-2 flex-shrink-0 shadow-sm"
                  style={{ borderColor: roleTheme.accent }}
                >
                  {user?.avatar ? (
                    <Image
                      src={user.avatar}
                      alt={user.name || "User"}
                      width={40}
                      height={40}
                      className="object-cover"
                    />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: roleTheme.accent }}
                    >
                      <User size={20} className="text-white" />
                    </div>
                  )}
                </div>
                <div className="hidden sm:block text-left min-w-0 max-w-[120px]">
                  <div className="font-medium text-base text-gray-800 truncate leading-tight">
                    {user.name ? user.name.split(" ")[0] : ""}
                  </div>
                  <div className="text-sm text-gray-600 truncate">
                    {roleLabel}
                  </div>
                </div>
              </button>

              <UserMenuDropdown
                open={userMenuOpen}
                onClose={() => setUserMenuOpen(false)}
                onLogout={onLogout}
                onProfileClick={onProfileClick}
                user={user}
                accent={roleTheme.accent}
              />
            </div>
          </div>
        </header>

        <motion.main
          className="flex-1 px-4 sm:px-6 py-6 pt-20 pb-16 lg:pb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="max-w-6xl mx-auto w-full">
            <AnimatePresence mode="wait">
              {isLoading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-64 flex items-center justify-center"
                >
                  <div className="flex flex-col items-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1.5,
                        ease: "linear",
                        repeat: Infinity,
                      }}
                      className="w-10 h-10 border-4 border-t-transparent border-blue-500 rounded-full"
                    />
                    <motion.p
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="mt-2 text-gray-500 text-base"
                    >
                      Loading content...
                    </motion.p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="content"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    type: "spring",
                    stiffness: 100,
                    damping: 15,
                  }}
                >
                  {children}
                </motion.div>
              )}
            </AnimatePresence>
            <HeroBanner roleTheme={roleTheme} targetRole={targetRole} />
          </div>
        </motion.main>

        <footer className="mt-8 py-6 text-center text-base text-gray-500 relative z-10 bg-white border-t">
          <p>© {new Date().getFullYear()} IndeMenu. All rights reserved.</p>
          <div className="flex flex-wrap justify-center gap-4 mt-2">
            <Link
              href="/pages/privacy"
              className="hover:text-gray-700 transition-colors"
            >
              Privacy
            </Link>
            <Link
              href="/pages/terms"
              className="hover:text-gray-700 transition-colors"
            >
              Terms
            </Link>
            <Link
              href="/pages/contact"
              className="hover:text-gray-700 transition-colors"
            >
              Support
            </Link>
          </div>
        </footer>

        <EnhancedMobileNav
          navItems={navItems}
          pathname={pathname}
          roleTheme={roleTheme}
        />
      </div>
    </div>
  );
}

export default memo(ModernDashboardLayout); 